import { useState } from "react";
import { initials } from "../api/queries";

/**
 * A person's avatar (Revision 94): their profile picture when they have one, otherwise their initials. Decorative: the
 * name is always written next to it (or in the control's accessible name), so it is hidden from assistive technology.
 * A picture that fails to load (expired, storage off) falls back to the initials.
 *
 * Crew roles (Revision 104): `isAgent` draws the agent ring — an animated rainbow conic gradient around the avatar, still
 * (no rotation) when the viewer prefers reduced motion. The ring takes no extra room (it sits in a negative margin), so an
 * agent's avatar lines up with everyone else's. The "· AI" attribution next to their actions stays the accessible signal.
 */
export function PersonAvatar({
  name,
  avatarUrl,
  className,
  isAgent,
  onPictureFailed,
}: {
  name: string;
  avatarUrl?: string | null;
  className: string;
  isAgent?: boolean;
  /** The picture did not load and the initials show instead (D-S2-01: a host whose label starts with the visible text). */
  onPictureFailed?: (avatarUrl: string) => void;
}) {
  const [failed, setFailed] = useState<string | null>(null);
  const face =
    avatarUrl && failed !== avatarUrl ? (
      <span className={`${className} gf-av-img`} aria-hidden="true">
        <img
          src={avatarUrl}
          alt=""
          loading="lazy"
          onError={() => {
            setFailed(avatarUrl);
            onPictureFailed?.(avatarUrl);
          }}
        />
      </span>
    ) : (
      <span className={className} aria-hidden="true">
        {initials(name)}
      </span>
    );
  if (!isAgent) return face;
  return (
    <span className="gf-agent-ring" data-agent="true" aria-hidden="true">
      {face}
    </span>
  );
}
